import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAccountData } from '../../redux/main/accountSlice';
import { setGoingToClose, setHasToRefresh, setModal, selectRightModalOldState, swapModal } from '../../redux/rightModalSlice';
import { useColorPalette } from '../../hooks/useColorPalette';
import CustomSelectElement from '../CustomSelectElement';
import { getNodeApiUrl } from '../../config/api.config.js';

const initialState = {
    fiscal: false,
    alias: "",
    addressTypeLabel: "",
    address: "",
    phone: "",
    mobile: "",
    email: ""
};

export default function AddEditAddress({ customerId, customerData = null }) {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const rightModalOldState = useSelector(selectRightModalOldState);
    
    // Stati per il form
    const [formInput, setFormInput] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Validazione campi obbligatori
    const [isValid, setIsValid] = useState(false);
    
    useEffect(() => {
        const requiredFields = ['alias', 'address'];
        const allRequiredFieldsValid = requiredFields.every(field => {
            const value = formInput[field];
            return value && value.trim() !== '';
        });
        setIsValid(allRequiredFieldsValid);
    }, [formInput]);
    
    const handleFormInput = (event) => {
        const { id, value, checked } = event?.target;
        const fixedValue = id === 'fiscal' ? checked : value;
        setFormInput(prevState => ({
            ...prevState,
            [id]: fixedValue
        }));
    };
    
    const handleSubmit = async () => {
        if (!isValid) {
            setError('Compila tutti i campi obbligatori');
            return;
        }
        
        if (!customerId) {
            setError('ID cliente mancante');
            return;
        }
        
        setIsSubmitting(true);
        setError('');
        
        try {
            // Chiamata API per aggiungere l'indirizzo al cliente
            const apiUrl = getNodeApiUrl('customers/addAddress');
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({
                    customerId: customerId,
                    address: formInput
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Errore durante l\'aggiunta dell\'indirizzo');
            }
            
            const result = await response.json();
            console.log('Indirizzo aggiunto con successo:', result);
            
            // Ottieni l'ID dell'indirizzo appena creato dalla risposta
            let newAddressId = null;
            let newAddressLabel = formInput.alias;
            
            // La risposta potrebbe contenere l'indirizzo in diversi formati
            if (result.address && result.address._id) {
                newAddressId = result.address._id;
            } else if (result._id) {
                newAddressId = result._id;
            } else if (result.data && result.data._id) {
                newAddressId = result.data._id;
            }
            
            // Salva i dati dell'indirizzo creato per permettere la selezione automatica
            dispatch(setModal({
                mode: 'add',
                data: {
                    addressId: newAddressId,
                    addressLabel: `${formInput.alias} - ${formInput.address}`,
                    addressData: {
                        ...formInput,
                        _id: newAddressId
                    }
                }
            }));
            
            setSuccessMessage('Indirizzo aggiunto con successo!');
            toast.success('Indirizzo aggiunto con successo!');
            
            console.log('✅ Indirizzo creato con successo, dati completi:', result);
            console.log('✅ Nuovo indirizzo:', result.address);
            
            // Chiudi l'offcanvas dopo 1.5 secondi
            setTimeout(() => {
                dispatch(setHasToRefresh());
                
                // Se c'è uno stato precedente (modale aperta da un'altra modale), torna indietro
                if (rightModalOldState && rightModalOldState.oldMode) {
                    console.log('Tornando alla modale precedente con dati indirizzo:', {
                        addressData: result.address
                    });
                    // Passa i dati del nuovo indirizzo a swapModal
                    dispatch(swapModal({
                        addressData: result.address // swapModal unirà questi dati con oldData
                    }));
                } else {
                    // Altrimenti chiudi completamente
                    dispatch(setGoingToClose());
                }
            }, 1500);
            
        } catch (err) {
            console.error('Errore durante l\'aggiunta dell\'indirizzo:', err);
            setError(err.message || 'Errore durante l\'aggiunta dell\'indirizzo');
            toast.error(err.message || 'Errore durante l\'aggiunta dell\'indirizzo');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        <div className="p-4">
            <div className="mb-4">
                <h3 className="text-xl font-semibold" style={{ color: palette.primary }}>
                    <i className="fa-solid fa-map-marker-alt mr-2"></i>
                    Aggiungi Nuovo Indirizzo
                </h3>
                {customerData && (
                    <p className="text-sm text-gray-600 mt-2">
                        Cliente: <strong>{customerData.label || customerData.customerLabel}</strong>
                    </p>
                )}
            </div>
            
            {/* Messaggi di errore e successo */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center text-red-800">
                        <i className="fa-solid fa-exclamation-circle mr-2"></i>
                        <span>{error}</span>
                    </div>
                </div>
            )}
            
            {successMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center text-green-800">
                        <i className="fa-solid fa-check-circle mr-2"></i>
                        <span>{successMessage}</span>
                    </div>
                </div>
            )}
            
            {/* Form per l'aggiunta dell'indirizzo */}
            <div className="space-y-4">
                {/* Checkbox indirizzo di fatturazione */}
                <div className={`p-3 border rounded-lg ${formInput.fiscal ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}>
                    <div className="flex items-center">
                        <input 
                            type="checkbox" 
                            id="fiscal" 
                            checked={formInput.fiscal} 
                            onChange={handleFormInput}
                            className="mr-2"
                        />
                        <label htmlFor="fiscal" className="font-medium">
                            Indirizzo di fatturazione
                        </label>
                    </div>
                </div>
                
                {/* Alias e Tipo di indirizzo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="input-box">
                        <label htmlFor="alias" className="block text-sm font-medium mb-1">
                            Alias <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" 
                            id="alias" 
                            onChange={handleFormInput} 
                            value={formInput.alias}
                            placeholder="es. Sede principale"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    
                    <div className="input-box">
                        <label htmlFor="addressTypeLabel" className="block text-sm font-medium mb-1">
                            Tipo di indirizzo
                        </label>
                        {accountData?.settings?.addressTypes ? (
                            <CustomSelectElement
                                id="addressTypeLabel"
                                name="addressTypeLabel"
                                list={accountData.settings.addressTypes}
                                setter={(value) => {
                                    setFormInput(prev => ({
                                        ...prev,
                                        addressTypeLabel: value
                                    }));
                                }}
                                defaultValue={formInput.addressTypeLabel}
                                valueKey="label"
                                labelKey="label"
                            />
                        ) : (
                            <input 
                                type="text" 
                                id="addressTypeLabel" 
                                onChange={handleFormInput} 
                                value={formInput.addressTypeLabel}
                                placeholder="es. Sede operativa"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                    </div>
                </div>
                
                {/* Indirizzo */}
                <div className="input-box">
                    <label htmlFor="address" className="block text-sm font-medium mb-1">
                        Indirizzo <span className="text-red-500">*</span>
                    </label>
                    <input 
                        type="text" 
                        id="address" 
                        onChange={handleFormInput} 
                        value={formInput.address}
                        placeholder="es. Via Roma 123, 00100 Roma RM"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                
                {/* Telefono e Cellulare */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="input-box">
                        <label htmlFor="phone" className="block text-sm font-medium mb-1">
                            Telefono
                        </label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 rounded-l-md">
                                <i className="fa-solid fa-phone text-gray-500"></i>
                            </span>
                            <input 
                                type="text" 
                                id="phone" 
                                onChange={handleFormInput} 
                                value={formInput.phone || ""}
                                placeholder="Inserisci telefono"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    
                    <div className="input-box">
                        <label htmlFor="mobile" className="block text-sm font-medium mb-1">
                            Cellulare
                        </label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 rounded-l-md">
                                <i className="fa-solid fa-mobile-alt text-gray-500"></i>
                            </span>
                            <input 
                                type="text" 
                                id="mobile" 
                                onChange={handleFormInput} 
                                value={formInput.mobile || ""}
                                placeholder="Inserisci cellulare"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>
                
                {/* Email */}
                <div className="input-box">
                    <label htmlFor="email" className="block text-sm font-medium mb-1">
                        Email
                    </label>
                    <div className="flex">
                        <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 rounded-l-md">
                            <i className="fa-solid fa-envelope text-gray-500"></i>
                        </span>
                        <input 
                            type="email" 
                            id="email" 
                            onChange={handleFormInput} 
                            value={formInput.email || ""}
                            placeholder="Inserisci email"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
                
                {/* Pulsanti */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <button
                        type="button"
                        onClick={() => {
                            // Se c'è uno stato precedente, torna indietro, altrimenti chiudi
                            if (rightModalOldState && rightModalOldState.oldMode) {
                                dispatch(swapModal());
                            } else {
                                dispatch(setGoingToClose());
                            }
                        }}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                        <i className="fa-solid fa-times mr-2"></i>
                        Annulla
                    </button>
                    
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!isValid || isSubmitting}
                        style={{
                            backgroundColor: isValid && !isSubmitting ? palette.primary : undefined,
                            color: isValid && !isSubmitting ? palette.textOnPrimary : undefined
                        }}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            isValid && !isSubmitting 
                                ? 'hover:opacity-90' 
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-plus mr-2"></i>
                                Aggiungi Indirizzo
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
